import { Dialog } from '@angular/cdk/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { selectProfileState } from 'src/app/stores/profile/profile.selector';
import {
  ComponentCredentialComponentDto,
  ComponentCredentialDto,
} from 'src/app/models/component-credentials.model';
import { AlertService } from 'src/app/services/alert.service';
import { ComponentCredentialsService } from 'src/app/services/component-credentials.service';
import { ComponentCredentialsStore } from 'src/app/services/component-credentials.store';
import { matchesSearch } from 'src/app/utils/text-search';
import { InformationModalComponent } from '../modals/information-modal/information-modal.component';

@Component({
  selector: 'app-component-credentials-list',
  templateUrl: './component-credentials-list.component.html',
  styleUrls: ['./component-credentials-list.component.scss'],
  standalone: false,
})
export class ComponentCredentialsListComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly alertService = inject(AlertService);
  private readonly componentCredentialsService = inject(
    ComponentCredentialsService,
  );
  protected readonly componentCredentialsStore = inject(
    ComponentCredentialsStore,
  );
  private readonly profile = toSignal(inject(Store).select(selectProfileState));

  searchText = '';

  /** A support user reads the credentials; only a masteradmin may change or validate one. */
  get canManageCredentials(): boolean {
    return this.profile()?.isMasteradmin ?? false;
  }

  constructor() {
    effect(() => {
      const error = this.componentCredentialsStore.error();
      if (!error) {
        return;
      }

      this.alertService.error(this.getErrorMessage(error));
      this.componentCredentialsStore.clearError();
    });
  }

  ngOnInit(): void {
    this.componentCredentialsStore.getAllComponentCredentials();
  }

  get filteredCredentials(): ComponentCredentialDto[] {
    return this.componentCredentialsStore
      .credentials()
      .filter((credential) =>
        matchesSearch(this.searchText, [
          credential.id,
          this.getComponentName(credential.component),
          credential.description,
          credential.scope?.id,
          credential.scope?.name,
          credential.clientInfo?.clientId,
        ]),
      );
  }

  onSearchChange(searchText: string): void {
    this.searchText = searchText;
  }

  navigateToCreateCredential(): void {
    this.router.navigate(['/admin/licenses/component-credentials/create']);
  }

  navigateToEditCredential(credentialId: string): void {
    this.router.navigate([
      '/admin/licenses/component-credentials',
      credentialId,
      'edit',
    ]);
  }

  deleteCredential(credentialId: string): void {
    const credential =
      this.componentCredentialsStore
        .credentials()
        .find((item) => item.id === credentialId) ?? null;
    const label =
      this.getComponentName(credential?.component) ||
      'this component credential';

    const dialogRef = this.dialog.open(InformationModalComponent, {
      data: {
        headerText: 'Delete Component Credential',
        bodyText: `Are you sure you want to delete "${label}"?\n\nThis action cannot be undone.`,
        cancelable: true,
      },
    });

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      const dialogResult = result as { cancel?: boolean } | undefined;
      if (dialogResult?.cancel === false) {
        this.componentCredentialsStore.deleteComponentCredential(credentialId);
      }
    });
  }

  validateCredential(credentialId: string): void {
    if (!credentialId) {
      return;
    }

    this.componentCredentialsService
      .validateCredential({ id: credentialId })
      .pipe(take(1))
      .subscribe({
        next: (validationResponse) => {
          if (validationResponse.ok === false) {
            this.openValidationFailedModal(credentialId);
            return;
          }

          this.alertService.success('Credential validation passed.');
        },
        error: (error: unknown) => {
          this.alertService.error(
            this.getErrorMessage(
              error,
              'Failed to validate component credential.',
            ),
          );
        },
      });
  }

  private openValidationFailedModal(credentialId: string): void {
    const dialogRef = this.dialog.open(InformationModalComponent, {
      data: {
        headerText: 'Credential Validation Failed',
        bodyText:
          'This credential failed validation. Please update it to ensure the integration works correctly.',
        cancelable: true,
        cancelButtonText: 'Later',
        confirmButtonText: 'Edit',
      },
    });

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      const dialogResult = result as { cancel?: boolean } | undefined;
      if (dialogResult?.cancel === false) {
        this.navigateToEditCredential(credentialId);
      }
    });
  }

  getComponentName(component?: ComponentCredentialComponentDto | null): string {
    return component?.componentName?.trim() || 'Unknown Component';
  }

  getErrorMessage(
    error: unknown,
    fallbackMessage: string = 'Failed to load component credentials.',
  ): string {
    if (!error) {
      return '';
    }

    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string' && error.error.trim()) {
        return error.error.trim();
      }
      if (
        typeof error.error === 'object' &&
        error.error &&
        typeof (error.error as { message?: unknown }).message === 'string'
      ) {
        return (error.error as { message: string }).message;
      }

      return fallbackMessage;
    }

    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        message?: string;
        error?: { message?: string };
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }

    return fallbackMessage;
  }

  trackByCredential(index: number, credential: ComponentCredentialDto): string {
    return (
      credential.id ?? `${credential.component?.id ?? 'credential'}-${index}`
    );
  }
}
